import { StatusIndicator } from '@/components/StatusIndicator';
import { WarningIcon } from '@/components/icons';
import { certificateStatusMap } from '@/config/statusMaps';
import { getExpiryLevel } from '@/config/resourceRules';
import { formatDate, getDaysUntil } from '@/utils/date';

/**
 * Certificate status with an "expiring soon" badge for active certificates
 */
export function CertificateStatusIndicator({
  status,
  expiresAt,
}: {
  status: string;
  expiresAt: string | null;
}) {
  const daysUntilExpiry = getDaysUntil(expiresAt);
  const level = getExpiryLevel(daysUntilExpiry);
  const isExpiringSoon =
    daysUntilExpiry !== null && daysUntilExpiry > 0 && (level === 'warning' || level === 'critical');

  return (
    <div className="certs-status-wrapper">
      <StatusIndicator status={status} map={certificateStatusMap} baseClassName="certs-status" />
      {status === 'active' && isExpiringSoon && (
        <span className="certs-expiring-badge" title={`Expires in ${daysUntilExpiry} days`}>
          <WarningIcon />
          {daysUntilExpiry}d
        </span>
      )}
    </div>
  );
}

/**
 * Time left until expiry, color-coded by urgency
 */
export function ExpirationIndicator({ expiresAt }: { expiresAt: string | null }) {
  const daysUntilExpiry = getDaysUntil(expiresAt);
  const level = getExpiryLevel(daysUntilExpiry);

  switch (level) {
    case 'unknown':
      return <span className="certs-expiry certs-expiry-unknown">-</span>;
    case 'expired':
      return (
        <span className="certs-expiry certs-expiry-expired">
          Expired {Math.abs(daysUntilExpiry ?? 0)}d ago
        </span>
      );
    case 'critical':
      return <span className="certs-expiry certs-expiry-critical">{daysUntilExpiry}d left</span>;
    case 'warning':
      return <span className="certs-expiry certs-expiry-warning">{daysUntilExpiry}d left</span>;
    default:
      return <span className="certs-expiry certs-expiry-ok">{formatDate(expiresAt)}</span>;
  }
}
